import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const LurganAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Lurgan | Stress Hypnotherapy Historic Market Town</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Lurgan from Belfast clinic (27 miles). Evidence-based treatment for anxiety disorders, panic, work stress. CNHC registered. Historic market town support."
        />
        <meta name="keywords" content="anxiety treatment Lurgan, stress management Lurgan, hypnotherapy anxiety Lurgan, panic attacks Lurgan County Armagh, therapist Lurgan stress, work anxiety Lurgan" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lurgan-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Lurgan | Stress Hypnotherapy Historic Market Town" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Lurgan from Belfast. Evidence-based treatment for anxiety disorders, panic, work stress." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety and Stress Treatment Lurgan",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Lurgan from Belfast clinic. Specializing in anxiety disorders, panic attacks, work stress, and supporting the historic market town community with working-class values.",
            "url": "https://www.sharonclare-hypnotherapy.com/lurgan-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Lurgan",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Lurgan from your Belfast clinic for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Lurgan is approximately 27 miles from the Belfast clinic via the M1 motorway, taking 35-40 minutes by car. This manageable distance makes regular anxiety treatment appointments entirely practical. Many Lurgan clients find the journey worthwhile for specialist care - some even describe the drive as therapeutic self-care time away from daily stressors."
                }
              },
              {
                "@type": "Question",
                "name": "Can I do anxiety treatment sessions online instead of driving to Belfast?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Lurgan residents who prefer the convenience of online therapy or cannot make the drive to Belfast on a particular week. Online sessions provide the same professional, evidence-based anxiety treatment as in-person sessions. This flexibility is ideal for clients with caring responsibilities, shift work patterns, or those who simply prefer online therapy."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for long-term anxiety problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy is highly effective for both short-term and long-term anxiety. It works by calming your overactive fight-or-flight response, helping you understand triggers, and creating new, healthier thought patterns. Most clients notice significant improvements within 4-6 sessions, even with anxiety that's been present for years. The approach addresses underlying causes rather than just managing symptoms."
                }
              },
              {
                "@type": "Question",
                "name": "How does living in Lurgan affect mental health and anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Lurgan's identity as a historic PRE-1965 market town, with its working-class community values and traditional bonds, creates both strengths and challenges. While the strong community connections can be supportive, economic pressures, retail sector stress, and the complexities of the Craigavon administrative merge can contribute to anxiety. Many Lurgan residents also commute to Belfast for work, adding M1 commuter stress to daily life. Hypnotherapy helps build resilience to these specific pressures."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with work stress and shift work anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for work-related stress and anxiety from shift work patterns. Many Lurgan residents work in retail, service industries, or commute to Belfast, dealing with irregular hours, customer-facing pressures, and work-life balance challenges. Solution-focused hypnotherapy helps calm the stress response, improve sleep quality despite shift patterns, build resilience, and develop healthier coping strategies for workplace pressures."
                }
              },
              {
                "@type": "Question",
                "name": "Is anxiety treatment completely confidential?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. All sessions are completely confidential and protected by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Whether you attend in-person at the Belfast clinic or online from home in Lurgan, your privacy is fully protected. Nothing discussed in sessions is shared with anyone without your explicit consent (except in rare legal requirements around safeguarding)."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Lurgan
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Lurgan's historic market town community from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">27 miles</span> from Lurgan via M1 • <span className="font-semibold">35-40 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for convenience</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Lurgan */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Lurgan
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Lurgan and struggling with anxiety or stress, you're not alone. With a population of around 27,000, Lurgan maintains its identity as a historic market town that predates the 1965 Craigavon development. This strong sense of place and working-class community values creates both support and unique pressures that can significantly impact mental health.
              </p>
              <p>
                Many Lurgan residents experience anxiety related to economic pressures in a post-industrial market town. The retail sector dominates local employment, with shift work patterns, customer-facing stress, and job insecurity creating constant worry. For those who commute to Belfast along the M1, there's the added burden of daily traffic anxiety, time poverty, and the exhausting balance between work demands and family life. The working-class community, while supportive, can sometimes make it difficult to openly discuss mental health struggles or seek help without feeling judged.
              </p>
              <p>
                Living in Lurgan means navigating the complexity of being part of the administrative Craigavon borough while maintaining a distinct historic identity. This can create a subtle sense of disconnection or confusion about belonging. Add financial worries, family responsibilities, shift work exhaustion, and the physical symptoms of chronic stress - tight chest, racing heart, poor sleep, constant tension - and it's no wonder that anxiety can feel overwhelming and all-consuming.
              </p>
              <p>
                While I don't have a physical clinic in Lurgan itself, I'm proud to serve the Lurgan community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. The journey from Lurgan is straightforward - approximately 27 miles north via the M1 motorway, taking 35-40 minutes. For Lurgan residents, this provides accessible specialist anxiety treatment at a manageable distance. Many clients find the journey becomes part of their self-care routine - dedicated time away from daily pressures to focus on their wellbeing. For those who prefer the convenience of staying local, or have caring responsibilities or shift work making travel difficult, I also offer online sessions that provide the same professional treatment from the comfort of your home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Lurgan Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Difficulty falling or staying asleep',
                    'Racing thoughts, especially at night',
                    'Physical symptoms: tight chest, pounding heart, tense shoulders',
                    'Constant sense of unease or dread',
                    'Avoiding situations out of fear or worry',
                    'Feeling overwhelmed by everyday tasks',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Lurgan-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Retail sector stress and shift work pressures',
                    'Economic insecurity in post-industrial market town',
                    'M1 commuter stress for Belfast workers',
                    'Work-life balance challenges with family',
                    'Customer-facing role emotional exhaustion',
                    'Historic identity vs administrative confusion',
                    'Financial anxiety and working-class pressures',
                    'Community expectations and stigma around mental health'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                When anxiety and stress take over your life, it can feel impossible to break free. The racing thoughts, the physical tension, the exhaustion from constant worry - it affects everything from your sleep to your relationships to your ability to cope with daily challenges. But there is hope. You don't have to live like this. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you regain control and find lasting calm.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety: What's Really Happening
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Anxiety is incredibly common - around 1 in 4 people in the UK will experience an anxiety disorder at some point in their lives. But just because it's common doesn't make it any easier to live with. Anxiety is your brain's natural alarm system going into overdrive, interpreting everyday situations as serious threats and triggering your fight-or-flight response even when there's no real danger present.
              </p>
              <p>
                When you're anxious, your brain releases stress hormones like cortisol and adrenaline, preparing your body to respond to a perceived threat. This is why you experience physical symptoms - racing heart, tight chest, shallow breathing, muscle tension, digestive issues. Your brain is literally preparing you to fight or run away, even when you're just sitting at home worrying or lying in bed unable to sleep.
              </p>
              <p>
                Anxiety disorders include generalized anxiety disorder (persistent, excessive worry about everyday things), social anxiety (fear of social situations and judgment), panic disorder (sudden, intense panic attacks), and specific phobias. Many people experience a combination of symptoms that don't fit neatly into one category. What matters is not the label, but the impact it's having on your quality of life and your ability to function day-to-day.
              </p>
              <p>
                The good news is that anxiety is highly treatable. Your brain created these anxious patterns, and your brain can learn new, healthier patterns. Solution-focused hypnotherapy works by calming the overactive stress response, helping you understand your triggers, and creating lasting change in how you think about and respond to stress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, and clarity returns. It's not about dwelling on the past or analyzing every worry - it's about building the resilience and calm you need to move forward.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms like racing heart, tight chest, and tense muscles. Your body learns to relax again, and the constant state of high alert fades.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Rewire Thought Patterns',
                  text: 'Build new habits of thought and behavior that support resilience and wellbeing. Replace anxious, catastrophic thinking with calmer, more balanced responses to stress and challenge.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Create a calmer pre-sleep routine and quiet the racing mind that keeps you awake at night. Wake feeling more refreshed and better equipped to handle daily challenges with resilience.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-white rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl text-center">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety disorder',
                    'Social anxiety and isolation',
                    'Panic attacks and panic disorder',
                    'Work-related stress and burnout',
                    'Retail sector anxiety and customer-facing stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Commuter stress and travel anxiety',
                    'Sleep problems and insomnia from worry',
                    'Performance anxiety and confidence issues',
                    'Financial anxiety and economic stress',
                    'Family stress and relationship tension'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Sharon's Approach to Anxiety Treatment
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. My background in nursing means I understand the medical side of anxiety - the neurological processes, the physical symptoms, the interactions with other health conditions. But more importantly, I understand the human side: the fear, the exhaustion, the desperate need for relief and the courage it takes to seek help.
              </p>
              <p>
                I use solution-focused hypnotherapy, an evidence-based approach combining talking therapy with hypnosis. Each session begins with structured conversation about your week, focusing on what's working, what you want to achieve, and practical steps forward. This isn't about dwelling on problems or analyzing every worry - it's about building momentum toward positive change. Then we move into hypnosis, a deeply relaxed state where your subconscious mind becomes more receptive to positive suggestions and new patterns of thinking.
              </p>
              <p>
                Hypnosis isn't sleep, and it's not stage hypnosis. You remain completely aware and in control throughout. It's simply a state of focused attention and deep relaxation - similar to being absorbed in a good book or daydreaming on a familiar journey. In this relaxed state, we work with your subconscious mind to calm the overactive anxiety response, build resilience, and create lasting change in how you think about and respond to stress.
              </p>
              <p>
                Most clients notice improvements within 4-6 sessions, though everyone's journey is different. Some people feel calmer after just one or two sessions. Others need a bit more time to build lasting change. What matters is that you're moving forward at your own pace, supported by evidence-based treatment that addresses both the symptoms and the underlying causes of anxiety. You don't have to live with constant worry and fear. Help is available, and lasting change is absolutely possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Lurgan
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway northbound from Junction 10 into Belfast, then south along the Ormeau Road
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 27 miles through Moira and Lisburn
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 35-40 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Manageable Distance for Regular Treatment</h3>
                <p className="text-gray-700">
                  At 27 miles via the M1 motorway, the journey from Lurgan to Belfast is genuinely practical for regular anxiety treatment appointments. Many Lurgan clients find the drive manageable and worthwhile for specialist care. Some even share that the journey becomes therapeutic - dedicated time away from daily pressures, work stress, and family responsibilities to focus on yourself and your wellbeing. The drive there can be time to prepare mentally, and the drive home time to reflect on the session and practice the calm you've built.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer not to drive to Belfast? Can't make the journey on a particular week due to shift work or caring responsibilities? I offer secure online hypnotherapy sessions via video call. Online sessions provide exactly the same professional, evidence-based anxiety treatment with the convenience of attending from your home in Lurgan. This option is ideal for those with mobility concerns, irregular work hours, childcare responsibilities, or anyone who simply prefers the flexibility and comfort of online therapy. The treatment is just as effective - only the location changes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Lurgan Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd been struggling with anxiety for years - work stress from retail, money worries, not sleeping properly. I was always on edge, snapping at my family, feeling exhausted all the time. Sharon's approach was so different from what I expected. After four sessions, I'm sleeping through the night, I can handle difficult customers without taking it home, and I actually feel calm. The anxiety is still there sometimes, but it doesn't control my life anymore. I wish I'd done this years ago."
                </p>
                <p className="font-semibold text-gray-900">J.M., County Armagh - Anxiety and Work Stress</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The panic attacks were so bad I couldn't leave the house some days. My heart would start racing, I'd feel like I couldn't breathe, and I'd be convinced something terrible was about to happen. Sharon helped me understand what was happening in my brain and taught me how to calm myself down. After six sessions, I haven't had a full panic attack in months. I feel like I've got my life back. I chose online sessions because I was too anxious to drive to Belfast at first, and they worked brilliantly."
                </p>
                <p className="font-semibold text-gray-900">S.T., Craigavon Borough - Panic Attacks and Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Between commuting to Belfast for work, shift patterns that changed every week, and trying to be there for my kids, I was completely burnt out. Constant anxiety, couldn't switch off, wasn't sleeping. Sharon's hypnotherapy helped me manage the stress, set boundaries, and actually relax again. I'm still doing the same job and the same commute, but I handle it so much better now. The anxiety doesn't overwhelm me like it used to. It's made such a difference to my whole family."
                </p>
                <p className="font-semibold text-gray-900">P.K., County Armagh - Work-Life Balance and Commuter Stress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lurgan Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Lurgan from your Belfast clinic for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Lurgan is approximately 27 miles from the Belfast clinic via the M1 motorway, taking 35-40 minutes by car. This manageable distance makes regular anxiety treatment appointments entirely practical. Many Lurgan clients find the journey worthwhile for specialist care - some even describe the drive as therapeutic self-care time away from daily stressors.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I do anxiety treatment sessions online instead of driving to Belfast?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Lurgan residents who prefer the convenience of online therapy or cannot make the drive to Belfast on a particular week. Online sessions provide the same professional, evidence-based anxiety treatment as in-person sessions. This flexibility is ideal for clients with caring responsibilities, shift work patterns, or those who simply prefer online therapy.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for long-term anxiety problems?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy is highly effective for both short-term and long-term anxiety. It works by calming your overactive fight-or-flight response, helping you understand triggers, and creating new, healthier thought patterns. Most clients notice significant improvements within 4-6 sessions, even with anxiety that's been present for years. The approach addresses underlying causes rather than just managing symptoms.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does living in Lurgan affect mental health and anxiety?
                </h3>
                <p className="text-gray-700">
                  Lurgan's identity as a historic PRE-1965 market town, with its working-class community values and traditional bonds, creates both strengths and challenges. While the strong community connections can be supportive, economic pressures, retail sector stress, and the complexities of the Craigavon administrative merge can contribute to anxiety. Many Lurgan residents also commute to Belfast for work, adding M1 commuter stress to daily life. Hypnotherapy helps build resilience to these specific pressures.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with work stress and shift work anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for work-related stress and anxiety from shift work patterns. Many Lurgan residents work in retail, service industries, or commute to Belfast, dealing with irregular hours, customer-facing pressures, and work-life balance challenges. Solution-focused hypnotherapy helps calm the stress response, improve sleep quality despite shift patterns, build resilience, and develop healthier coping strategies for workplace pressures.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is anxiety treatment completely confidential?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. All sessions are completely confidential and protected by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Whether you attend in-person at the Belfast clinic or online from home in Lurgan, your privacy is fully protected. Nothing discussed in sessions is shared with anyone without your explicit consent (except in rare legal requirements around safeguarding).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm"
        subtitle="Take the first step toward less anxiety, less stress, and more peace. Book a free consultation today and discover how hypnotherapy can help you regain control and find calm in the chaos."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LurganAnxietyStress;
